import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Pagination from "../utils/Pagination";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSortUp,
  faSortDown,
  faChevronDown,
  faChevronRight,
  faFilter,
  faTimes,
  faMapMarkerAlt,
  faIdCard,
  faFileAlt,
  faClipboardList,
  faCog,
  faFileInvoice,
  faBuilding,
  faGlobe,
  faBarcode,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../buckland.png";
import { useParams } from "react-router-dom";
import MiniDashboardCrossedParts from "./MiniDashboardCrossedParts";
import { saveAs } from "file-saver";
import Breadcrumbs from "./Breadcrumbs";

const CustomerItemCatalogueCrossedParts = () => {
  const { customer_id, customer_rfc, item_number } = useParams();
  const [allItems, setAllItems] = useState([]);
  const [displayItems, setDisplayItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "" });
  const [filterVisible, setFilterVisible] = useState(false);
  const breadcrumbs = [
    { label: "Item Catalogue", path: "/customerItemCatalogueGrid" },
    { label: `Item Number: ${item_number}` },
  ];
  const [filters, setFilters] = useState({
    entry_point: [],
    license: [],
    pedimento: [],
    tipo_pedimento: [],
    operation_type: [],
    invoice_number: [],
    invoice_supplier: [],
    invoice_country: [],
    item_mx_hts_code: [],
  });
  const [statistics, setStatistics] = useState({
    countEntryPoints: 0,
    countLicenses: 0,
    countPedimentos: 0,
    countTipoPedimento: 0,
    countTrafficReferences: 0,
    averageDaysBetweenInvoices: 0,
  });

  const [selectOptions, setSelectOptions] = useState({});

  // Helper function to safely render values
  const renderCellValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    // Check if the value is an object
    if (typeof value === "object") {
      // Special handling for item_regulations array
      if (Array.isArray(value)) {
        // Check if this is an array of regulation objects
        if (value.length > 0 && value[0].regulation_code !== undefined) {
          return value
            .map((reg) => {
              const parts = [];
              if (reg.regulation_code) parts.push(reg.regulation_code);
              if (reg.regulation_number) parts.push(reg.regulation_number);
              if (reg.regulation_esignature)
                parts.push(`E-Sig: ${reg.regulation_esignature}`);
              return parts.join(" - ");
            })
            .join("; ");
        }
        // For other arrays, join the elements
        return value
          .map((item) => {
            if (typeof item === "object") {
              return JSON.stringify(item);
            }
            return item;
          })
          .join(", ");
      }

      // For single regulation objects
      if (
        value.regulation_code ||
        value.regulation_number ||
        value.regulation_esignature
      ) {
        const parts = [];
        if (value.regulation_code) parts.push(value.regulation_code);
        if (value.regulation_number) parts.push(value.regulation_number);
        if (value.regulation_esignature)
          parts.push(`E-Sig: ${value.regulation_esignature}`);
        return parts.join(" - ");
      }

      // For other objects, stringify them
      return JSON.stringify(value);
    }

    // For primitive values, return as is
    return value;
  };

  // Helper function to get label for select options
  const getSelectLabel = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    if (typeof value === "object") {
      if (Array.isArray(value)) {
        // Special handling for item_regulations array
        if (value.length > 0 && value[0].regulation_code !== undefined) {
          return value
            .map(
              (reg) => reg.regulation_code || reg.regulation_number || "No code"
            )
            .join(", ");
        }
        return value.join(", ");
      }

      // For regulation objects
      if (value.regulation_code || value.regulation_number) {
        return (
          value.regulation_code ||
          value.regulation_number ||
          JSON.stringify(value)
        );
      }

      return JSON.stringify(value);
    }

    return String(value);
  };

  useEffect(() => {
    fetchData();
  }, [customer_id, customer_rfc, item_number]);

  useEffect(() => {
    applyFilters();
  }, [allItems, filters]);

  const fetchData = async () => {
    setIsLoading(true);
    const accessToken = localStorage.getItem("accessToken");
    const params = new URLSearchParams({
      customer_key: customer_id,
      customer_rfc: customer_rfc,
      customer_item_number: item_number,
    });

    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_API_URL
        }/api/customer_item_catalogue/getCrossedParts?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setAllItems(response.data);
      initializeSelectOptions(response.data);
      applyFilters(response.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError("Failed to retrieve data");
    }
    setIsLoading(false);
  };

  const jsonToCSV = (json) => {
    const columns = Object.keys(json[0]);
    const header = columns.join(",");
    const rows = json.map((row) =>
      columns
        .map((fieldName) => {
          const value = row[fieldName];
          // Handle objects in CSV export
          if (typeof value === "object" && value !== null) {
            return JSON.stringify(JSON.stringify(value)); // Double stringify for CSV safety
          }
          return JSON.stringify(value, replacer);
        })
        .join(",")
    );
    return [header, ...rows].join("\r\n");
  };

  const replacer = (key, value) => (value === null ? "" : value);

  const handleDownloadCSV = () => {
    const csvData = jsonToCSV(displayItems);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "exported-data.csv");
  };

  const toggleFilters = () => {
    setFilterVisible(!filterVisible);
  };

  const clearFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => {
        // For object comparisons
        if (typeof item === "object" && typeof value === "object") {
          return JSON.stringify(item) !== JSON.stringify(value);
        }
        return item !== value;
      }),
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      entry_point: [],
      license: [],
      pedimento: [],
      tipo_pedimento: [],
      operation_type: [],
      invoice_number: [],
      invoice_supplier: [],
      invoice_country: [],
      item_mx_hts_code: [],
    });
  };

  const renderToggleIcon = () => (
    <FontAwesomeIcon
      icon={filterVisible ? faChevronDown : faChevronRight}
      onClick={toggleFilters}
      className="cursor-pointer text-gray-700 ml-2"
    />
  );

  const initializeSelectOptions = (data) => {
    const fields = [
      "entry_point",
      "license",
      "pedimento",
      "tipo_pedimento",
      "operation_type",
      "invoice_number",
      "invoice_supplier",
      "invoice_country",
      "item_mx_hts_code",
    ];
    const options = fields.reduce((opts, field) => {
      const uniqueValues = [
        ...new Set(
          data.map((item) => {
            const value = item[field];
            // For objects, stringify them to make them comparable
            if (typeof value === "object" && value !== null) {
              return JSON.stringify(value);
            }
            return value;
          })
        ),
      ];

      return {
        ...opts,
        [field]: uniqueValues.map((value) => {
          // Parse back stringified objects
          let actualValue = value;
          if (
            typeof value === "string" &&
            (value.startsWith("{") || value.startsWith("["))
          ) {
            try {
              actualValue = JSON.parse(value);
            } catch (e) {
              // Keep as string if parsing fails
            }
          }

          return {
            value: actualValue,
            label: getSelectLabel(actualValue),
          };
        }),
      };
    }, {});
    setSelectOptions(options);
  };

  const applyFilters = (data = allItems) => {
    const filteredData = data.filter((item) =>
      Object.entries(filters).every(([key, selectedValues]) => {
        if (selectedValues.length === 0) return true;

        const itemValue = item[key];

        return selectedValues.some((selectedValue) => {
          // Handle object comparisons
          if (
            typeof itemValue === "object" &&
            typeof selectedValue === "object"
          ) {
            return JSON.stringify(itemValue) === JSON.stringify(selectedValue);
          }
          return itemValue === selectedValue;
        });
      })
    );
    setDisplayItems(filteredData);
    setTotalPages(Math.ceil(filteredData.length / pageSize));
    calculateStatistics(filteredData);
  };

  const calculateStatistics = (items) => {
    const entryPoints = new Set(items.map((item) => item.entry_point));
    const licenses = new Set(items.map((item) => item.license));
    const pedimentos = new Set(items.map((item) => item.pedimento));
    const tipoPedimentos = new Set(items.map((item) => item.tipo_pedimento));
    const trafficReferences = new Set(
      items.map((item) => item.traffic_reference)
    );

    // Calculate average days between invoices
    const dates = items
      .map((item) => new Date(item.date_invoice_date))
      .filter((date) => !isNaN(date.getTime()))
      .map((date) => date.getTime())
      .sort((a, b) => a - b);

    let totalDays = 0;
    for (let i = 1; i < dates.length; i++) {
      totalDays += (dates[i] - dates[i - 1]) / 86400000;
    }
    const averageDays = dates.length > 1 ? totalDays / (dates.length - 1) : 0;

    setStatistics({
      countEntryPoints: entryPoints.size,
      countLicenses: licenses.size,
      countPedimentos: pedimentos.size,
      countTipoPedimento: tipoPedimentos.size,
      countTrafficReferences: trafficReferences.size,
      averageDaysBetweenInvoices: averageDays,
    });
  };

  const handleFilterChange = (field, selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      [field]: selectedOptions.map((option) => option.value),
    }));
  };

  const renderSelectFilters = () => (
    <div className="w-full bg-white rounded-lg shadow-sm p-5 mb-6 border border-gray-300">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          <FontAwesomeIcon icon={faFilter} className="mr-2 text-blue-600" />
          Search Filters
        </h2>
        <FontAwesomeIcon
          icon={filterVisible ? faChevronDown : faChevronRight}
          onClick={toggleFilters}
          className="cursor-pointer text-gray-700 hover:text-blue-600"
        />
      </div>

      {filterVisible && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
            {Object.keys(filters).map((fieldName) => {
              const iconMap = {
                entry_point: faMapMarkerAlt,
                license: faIdCard,
                pedimento: faFileAlt,
                tipo_pedimento: faClipboardList,
                operation_type: faCog,
                invoice_number: faFileInvoice,
                invoice_supplier: faBuilding,
                invoice_country: faGlobe,
                item_mx_hts_code: faBarcode,
              };

              return (
                <div key={fieldName} className="filter-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FontAwesomeIcon
                      icon={iconMap[fieldName]}
                      className="mr-1 text-blue-600"
                    />
                    {fieldName
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </label>
                  <Select
                    isMulti
                    name={fieldName}
                    options={selectOptions[fieldName]}
                    className="basic-multi-select"
                    classNamePrefix="select-filter"
                    onChange={(selectedOptions) =>
                      handleFilterChange(fieldName, selectedOptions || [])
                    }
                    value={selectOptions[fieldName]?.filter((option) =>
                      filters[fieldName].some((filterValue) => {
                        if (
                          typeof filterValue === "object" &&
                          typeof option.value === "object"
                        ) {
                          return (
                            JSON.stringify(filterValue) ===
                            JSON.stringify(option.value)
                          );
                        }
                        return filterValue === option.value;
                      })
                    )}
                    placeholder={`Filter by ${fieldName
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}`}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: "#e2e8f0",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#cbd5e1",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#e1effe",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#1e40af",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#60a5fa",
                        ":hover": {
                          backgroundColor: "#bfdbfe",
                          color: "#1e3a8a",
                        },
                      }),
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Active Filters Display */}
          {Object.entries(filters).some(([key, value]) => value.length > 0) && (
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex items-center flex-wrap">
                <span className="mr-2">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(filters).map(([key, selectedValues]) =>
                    selectedValues.map((value, idx) => {
                      const label = getSelectLabel(value);
                      return (
                        <span
                          key={`${key}-${idx}`}
                          className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center"
                        >
                          {key.replace(/_/g, " ")}: {label}
                          <button
                            onClick={() => clearFilter(key, value)}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <FontAwesomeIcon
                              icon={faTimes}
                              className="text-xs"
                            />
                          </button>
                        </span>
                      );
                    })
                  )}

                  <button
                    onClick={clearAllFilters}
                    className="text-red-600 hover:text-red-800 underline text-xs ml-2"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else {
      direction = "ascending";
    }
    setSortConfig({ key, direction });
  };

  const sortedItems = useMemo(() => {
    let sortableItems = [...displayItems];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle object sorting by stringifying
        if (typeof aValue === "object") aValue = JSON.stringify(aValue);
        if (typeof bValue === "object") bValue = JSON.stringify(bValue);

        if (aValue < bValue)
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (aValue > bValue)
          return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [displayItems, sortConfig]);

  const renderTableHeader = () => {
    if (displayItems.length === 0) return null;
    const numCols = Object.keys(displayItems[0]).length;
    return (
      <div
        className="bg-gray-200 px-6 py-3 grid items-center border-b border-gray-300 sticky top-0 z-10"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${numCols}, minmax(100px, 1fr))`,
        }}
      >
        {Object.keys(displayItems[0]).map((key, index) => (
          <div
            key={index}
            className="text-xs font-semibold text-gray-700 uppercase tracking-wider text-center py-2 cursor-pointer"
            onClick={() => handleSort(key)}
          >
            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            {sortConfig.key === key && (
              <FontAwesomeIcon
                icon={
                  sortConfig.direction === "ascending" ? faSortUp : faSortDown
                }
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderPagination = () => (
    <Pagination
      total={totalPages * pageSize}
      pageSize={pageSize}
      currentPage={currentPage}
      onPageChange={(page) => setCurrentPage(page)}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setCurrentPage(1);
      }}
    />
  );

  const renderHeader = () => (
    <div
      className="flex justify-between items-center px-4 py-3"
      style={{ backgroundColor: "#1A2654" }}
    >
      <img src={logo} alt="Company Logo" className="h-16" />
      <h1 className="text-xl font-semibold text-white">
        Crossed Parts Reports
      </h1>
    </div>
  );

  const renderTableRows = () => {
    const numCols =
      displayItems.length > 0 ? Object.keys(displayItems[0]).length : 0;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = sortedItems.slice(startIndex, endIndex);

    return paginatedItems.map((item, index) => (
      <div
        key={index}
        className={`grid grid-cols-${numCols} items-center text-center text-sm ${
          index % 2 === 0 ? "bg-white" : "bg-gray-50"
        } hover:bg-gray-100`}
        style={{
          gridTemplateColumns: `repeat(${numCols}, minmax(100px, 1fr))`,
        }}
      >
        {Object.values(item).map((value, colIndex) => (
          <div key={colIndex} className="text-xs px-1 py-2">
            {renderCellValue(value)}
          </div>
        ))}
      </div>
    ));
  };

  const renderContent = () => {
    if (isLoading) return <div className="text-center py-4">Loading...</div>;
    if (error)
      return (
        <div className="text-center py-4 text-red-500">Error: {error}</div>
      );
    if (displayItems.length === 0) {
      return (
        <div className="text-center py-4 text-gray-500">
          No data available for the selected criteria.
        </div>
      );
    }
    return (
      <div
        className="container mx-auto px-4 sm:px-8 py-8 bg-gray-100"
        style={{ maxWidth: "100%" }}
      >
        <MiniDashboardCrossedParts {...statistics} />
        {renderSelectFilters()}
        <div className="flex justify-end mb-4">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={handleDownloadCSV}
          >
            Export to CSV
          </button>
        </div>
        {renderPagination()}
        <div className="py-2">
          <div className="-mx-4 sm:-mx-8 px-4 sm:px-8 py-4 overflow-x-auto">
            <div className="inline-block min-w-full shadow-lg rounded-lg overflow-hidden">
              {renderTableHeader()}
              <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {renderTableRows()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      {renderHeader()}
      <div className="mx-auto px-4 py-8 bg-gray rounded-lg shadow-lg">
        <Breadcrumbs crumbs={breadcrumbs} />
      </div>
      {renderContent()}
    </>
  );
};

export default CustomerItemCatalogueCrossedParts;
